/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import type { ButtonHTMLAttributes, KeyboardEvent, ReactNode } from 'react';
import { createContext, useCallback, useContext, useId, useMemo, useRef, useState } from 'react';

interface TabsContextValue {
  value: string;
  setValue: (next: string) => void;
  tabId: (v: string) => string;
  panelId: (v: string) => string;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (ctx === null) {
    throw new Error('Tabs components must be used within Tabs');
  }
  return ctx;
}

export interface TabsProps {
  children: ReactNode;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
}

export function Tabs({
  children,
  value: controlledValue,
  defaultValue = '',
  onValueChange,
}: TabsProps) {
  const [internal, setInternal] = useState(defaultValue);
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internal;

  const setValue = useCallback(
    (next: string) => {
      if (!isControlled) {
        setInternal(next);
      }
      onValueChange?.(next);
    },
    [isControlled, onValueChange],
  );

  const baseId = useId();
  const tabId = useCallback((v: string) => `${baseId}-tab-${v}`, [baseId]);
  const panelId = useCallback((v: string) => `${baseId}-panel-${v}`, [baseId]);

  const ctx = useMemo(
    () => ({
      value,
      setValue,
      tabId,
      panelId,
    }),
    [value, setValue, tabId, panelId],
  );

  return <TabsContext.Provider value={ctx}>{children}</TabsContext.Provider>;
}

export interface TabListProps {
  children: ReactNode;
  className?: string;
}

export function TabList({ children, className }: TabListProps) {
  const { setValue } = useTabsContext();
  const listRef = useRef<HTMLDivElement>(null);

  const focusTabByIndex = (index: number) => {
    const tabs = [...(listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? [])];
    tabs[index]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const tabs = [...(listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? [])];
    if (tabs.length === 0) return;

    const active = document.activeElement;
    const currentIndex = tabs.indexOf(active as HTMLButtonElement);

    switch (e.key) {
      case 'ArrowRight': {
        e.preventDefault();
        const next = currentIndex >= 0 ? (currentIndex + 1) % tabs.length : 0;
        focusTabByIndex(next);
        const v = tabs[next]?.dataset.tabValue;
        if (v !== undefined) setValue(v);
        break;
      }
      case 'ArrowLeft': {
        e.preventDefault();
        const next =
          currentIndex >= 0 ? (currentIndex - 1 + tabs.length) % tabs.length : tabs.length - 1;
        focusTabByIndex(next);
        const v = tabs[next]?.dataset.tabValue;
        if (v !== undefined) setValue(v);
        break;
      }
      case 'Home': {
        e.preventDefault();
        focusTabByIndex(0);
        const v = tabs[0]?.dataset.tabValue;
        if (v !== undefined) setValue(v);
        break;
      }
      case 'End': {
        e.preventDefault();
        const last = tabs.length - 1;
        focusTabByIndex(last);
        const v = tabs[last]?.dataset.tabValue;
        if (v !== undefined) setValue(v);
        break;
      }
      case 'Enter':
      case ' ': {
        if (currentIndex >= 0) {
          e.preventDefault();
          const v = tabs[currentIndex]?.dataset.tabValue;
          if (v !== undefined) setValue(v);
        }
        break;
      }
      default:
        break;
    }
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      className={['ecs-tab-list', className].filter(Boolean).join(' ')}
      onKeyDown={onKeyDown}
    >
      {children}
    </div>
  );
}

export interface TabProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  value: string;
  children: ReactNode;
}

export function Tab({ value, children, className, ...rest }: TabProps) {
  const { value: selected, setValue, tabId, panelId } = useTabsContext();
  const selectedFlag = selected === value;
  const tid = tabId(value);
  const pid = panelId(value);

  return (
    <button
      {...rest}
      type="button"
      role="tab"
      id={tid}
      data-tab-value={value}
      aria-selected={selectedFlag}
      aria-controls={pid}
      tabIndex={selectedFlag ? 0 : -1}
      className={['ecs-tab', className].filter(Boolean).join(' ')}
      onClick={() => {
        setValue(value);
      }}
    >
      {children}
    </button>
  );
}

export interface TabPanelProps {
  value: string;
  children: ReactNode;
  className?: string;
}

export function TabPanel({ value, children, className }: TabPanelProps) {
  const { value: selected, tabId, panelId } = useTabsContext();
  const visible = selected === value;
  const tid = tabId(value);
  const pid = panelId(value);

  return (
    <div
      role="tabpanel"
      id={pid}
      aria-labelledby={tid}
      hidden={!visible}
      className={['ecs-tab-panel', className].filter(Boolean).join(' ')}
    >
      {children}
    </div>
  );
}
